import { useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Building2, Landmark, ShieldCheck, ShieldAlert, Search, Lock, Loader2, ArrowLeft, Crown,
} from "lucide-react";

const BROKERS = [
  "AAAFx Global Ltd", "Exness", "IC Markets", "XM", "Pepperstone", "FBS", "OctaFX", "Deriv",
  "RoboForex", "HFM (HotForex)", "FxPro", "Admiral Markets", "FXTM (ForexTime)",
  "Tickmill", "Vantage", "Eightcap", "BlackBull Markets", "Fusion Markets",
  "ThinkMarkets", "AvaTrade", "OANDA", "FOREX.com", "Alpari", "JustMarkets",
  "Axi", "Global Prime", "Traders Trust", "FXOpen", "Errante", "Fondex",
  "Purple Trading", "Windsor Brokers", "Libertex", "InstaForex", "Weltrade",
  "NordFX", "AMarkets", "SquaredFinancial", "CMC Markets", "Swissquote",
  "Dukascopy", "Interactive Brokers", "Saxo Bank", "Razor Markets", "Doo Prime",
  "Zero Markets", "Valutrades", "Skilling", "Scope Markets", "Hantec Markets",
  "IG", "FXCM", "Blueberry Markets", "Moneta Markets", "GO Markets", "TMGM",
  "VT Markets", "Trade Nation", "ACY Securities", "StarTrader", "TigerWit",
  "easyMarkets", "Capital.com", "MultiBank", "Equiti", "ATFX", "Grand Capital",
  "LiteFinance", "FIBO Group", "FreshForex", "ForexMart", "Tifia", "FXChoice",
  "Coinexx", "Hugo's Way", "OspreyFX", "IFC Markets", "Turnkey Forex", "PU Prime",
  "IUX Markets", "Headway", "SuperForex", "Land Prime", "Darwinex", "MEX Atlantic",
  "FXGiants", "Sway Markets", "GMI Markets", "N1CM", "Potrade Ltd", "AAA Fx",
];

const PROP_FIRMS = [
  "E8 Markets", "FTMO", "The5ers", "FundedNext", "Funding Pips", "FXIFY",
  "FunderPro", "Alpha Capital Group", "Goat Funded Trader", "Maven Trading",
  "Topstep", "Apex Trader Funding", "Other Prop Firm",
];

const PHASES = [
  { id: "evaluation", label: "Evaluation" },
  { id: "verification", label: "Verification" },
  { id: "funded", label: "Funded Account" },
];

function Combobox({ options, value, onChange, placeholder, icon: Icon = Building2, inputTestId, listTestId, optionPrefix }) {
  const [openList, setOpenList] = useState(false);
  const q = (value || "").trim().toLowerCase();
  const matches = (q ? options.filter((b) => b.toLowerCase().includes(q)) : options).slice(0, 60);
  const show = openList && matches.length > 0;
  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#5f7ba6] pointer-events-none" />
        <Input data-testid={inputTestId} value={value} inputMode="text"
          onChange={(e) => { onChange(e.target.value); setOpenList(true); }}
          onFocus={() => setOpenList(true)}
          onBlur={() => setTimeout(() => setOpenList(false), 150)}
          placeholder={placeholder} autoComplete="off"
          className="mt-1 pl-9 bg-[#060c18] border-[#14233d] rounded-xl h-12" />
      </div>
      {show && (
        <div data-testid={listTestId}
          className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-xl border border-[#14233d] bg-[#0A1120] shadow-2xl">
          {matches.map((b) => (
            <button key={b} type="button"
              data-testid={`${optionPrefix}-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(b); setOpenList(false); }}
              className="w-full text-left px-4 py-2.5 text-sm text-[#cfe0f5] hover:bg-[#0f1a2e] hover:text-white flex items-center gap-2 transition-colors">
              <Icon className="h-3.5 w-3.5 text-[#5f7ba6] shrink-0" /> {b}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ObjectiveRow({ label, hint, obj, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-[#5f7ba6]">{label}</Label>
      <div className="mt-1 flex gap-2">
        <Input type="text" inputMode="decimal" value={obj.value} data-testid={testid}
          onChange={(e) => onChange({ ...obj, value: e.target.value.replace(/[^0-9.]/g, "") })}
          placeholder={hint} className="bg-[#060c18] border-[#14233d] rounded-xl h-12 flex-1" />
        <div className="flex rounded-xl border border-[#14233d] overflow-hidden shrink-0">
          {["amount", "percent"].map((t) => (
            <button key={t} type="button" data-testid={`${testid}-${t}`}
              onClick={() => onChange({ ...obj, type: t })}
              className={`px-4 text-base font-bold transition-colors ${obj.type === t ? "text-white" : "text-[#5f7ba6] hover:text-white"}`}
              style={obj.type === t ? { backgroundColor: "var(--sfx-accent)" } : undefined}>
              {t === "amount" ? "$" : "%"}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ConnectAccount() {
  const { entitlements, refreshAll } = useOutletContext();
  const navigate = useNavigate();
  const propfirmLocked = !entitlements?.propfirm;
  const phases = entitlements?.propfirm_phases;

  const [tab, setTab] = useState("mt5");
  const [busy, setBusy] = useState(false);
  const [mt5, setMt5] = useState({ broker: "", account_id: "", password: "", server: "" });
  const [prop, setProp] = useState({
    firm_name: "", challenge_phase: "evaluation", account_id: "", password: "", server: "", balance: "100000",
    objectives: {
      daily_loss: { value: "", type: "percent" },
      max_drawdown: { value: "", type: "percent" },
      trailing_drawdown: { value: "", type: "amount" },
      profit_target: { value: "", type: "percent" },
    },
  });
  const setObj = (key, val) => setProp({ ...prop, objectives: { ...prop.objectives, [key]: val } });
  const phaseCfg = phases?.[prop.challenge_phase];

  const save = async () => {
    setBusy(true);
    try {
      const payload = tab === "mt5"
        ? { type: "mt5", broker: mt5.broker || "MetaTrader 5", account_id: mt5.account_id, login: mt5.account_id, password: mt5.password, server: mt5.server, platform: "MT5", balance: 10000 }
        : { type: "propfirm", firm_name: prop.firm_name, challenge_phase: prop.challenge_phase, account_id: prop.account_id, login: prop.account_id, password: prop.password, server: prop.server, platform: "MT5", balance: Number(prop.balance) || 100000, initial_balance: Number(prop.balance) || 100000, objectives: prop.objectives };
      await api.post("/account", payload);
      toast.success("MT5 account connected");
      await refreshAll();
      navigate("/app/account");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to connect account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-5 pt-6 pb-32 fade-up">
      <button onClick={() => navigate("/app/account")} data-testid="connect-back-button"
        className="flex items-center gap-2 text-sm text-[#8fb4e6] mb-4 hover:text-white transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to accounts
      </button>
      <h1 className="font-heading text-2xl font-bold tracking-tight">Connect MT5 Account</h1>
      <p className="text-[#5f7ba6] text-sm mt-1 mb-5">Enter your broker credentials to connect securely via MetaApi.</p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 bg-[#060c18] rounded-xl h-12 w-full">
          <TabsTrigger value="mt5" data-testid="tab-mt5" className="rounded-lg data-[state=active]:text-white" style={tab === "mt5" ? { backgroundColor: "var(--sfx-accent)" } : undefined}>MT5 Broker</TabsTrigger>
          <TabsTrigger value="propfirm" data-testid="tab-propfirm" className="rounded-lg data-[state=active]:text-white" style={tab === "propfirm" ? { backgroundColor: "var(--sfx-accent)" } : undefined}>Prop Firm</TabsTrigger>
        </TabsList>

        <TabsContent value="mt5" className="space-y-4 mt-5">
          <div><Label className="text-xs text-[#5f7ba6]">Broker (optional)</Label><Combobox options={BROKERS} value={mt5.broker} onChange={(v) => setMt5({ ...mt5, broker: v })} placeholder="Type your broker, e.g. Exness" inputTestId="mt5-broker" listTestId="broker-suggestions" optionPrefix="broker-option" /></div>
          <div><Label className="text-xs text-[#5f7ba6]">MT5 Login</Label><Input data-testid="mt5-account-id" inputMode="numeric" value={mt5.account_id} onChange={(e) => setMt5({ ...mt5, account_id: e.target.value })} placeholder="4025163" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-12" /></div>
          <div><Label className="text-xs text-[#5f7ba6]">Password (master — required to trade)</Label><Input data-testid="mt5-password" type="password" value={mt5.password} onChange={(e) => setMt5({ ...mt5, password: e.target.value })} placeholder="••••••••" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-12" /></div>
          <div><Label className="text-xs text-[#5f7ba6]">Broker Server</Label><Input data-testid="mt5-server" value={mt5.server} onChange={(e) => setMt5({ ...mt5, server: e.target.value })} placeholder="RazorMarkets-Live" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-12" /></div>
          <div className="flex items-start gap-2 text-xs text-[#5f7ba6] pt-1"><ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--sfx-accent)" }} /> Use your MASTER password (not investor / read-only) so the Smart Scalper can place trades.</div>
        </TabsContent>

        <TabsContent value="propfirm" className="space-y-4 mt-5">
          {propfirmLocked ? (
            <div className="border border-[#14233d] bg-[#060c18] rounded-2xl p-8 text-center" data-testid="propfirm-locked">
              <div className="h-14 w-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: "var(--sfx-accent-soft)" }}>
                <Lock className="h-6 w-6" style={{ color: "var(--sfx-accent)" }} />
              </div>
              <div className="font-heading font-semibold mb-1">Prop Firm Mode locked</div>
              <p className="text-xs text-[#5f7ba6] mb-4">Upgrade to Pro to unlock Prop Firm Mode with automated risk protection.</p>
              <Button data-testid="propfirm-upgrade-button" onClick={() => navigate("/app/settings")}
                className="h-10 rounded-full px-5 text-white font-semibold" style={{ backgroundColor: "var(--sfx-accent)" }}>
                <Crown className="mr-2 h-4 w-4" /> Upgrade to Pro
              </Button>
            </div>
          ) : (
            <>
              <div><Label className="text-xs text-[#5f7ba6]">Prop Firm</Label><Combobox options={PROP_FIRMS} value={prop.firm_name} onChange={(v) => setProp({ ...prop, firm_name: v })} placeholder="Type your prop firm, e.g. FTMO" icon={Landmark} inputTestId="prop-firm-name" listTestId="propfirm-suggestions" optionPrefix="propfirm-option" /></div>
              <div>
                <Label className="text-xs text-[#5f7ba6]">Account Phase</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {PHASES.map((ph) => {
                    const on = prop.challenge_phase === ph.id;
                    return (
                      <button key={ph.id} type="button" data-testid={`prop-phase-${ph.id}`}
                        onClick={() => setProp({ ...prop, challenge_phase: ph.id })}
                        className={`px-2 py-3 rounded-xl border text-xs font-semibold transition-colors ${on ? "text-white" : "border-[#14233d] text-[#7f9bc4]"}`}
                        style={on ? { borderColor: "var(--sfx-accent)", backgroundColor: "var(--sfx-accent-soft)" } : undefined}>
                        {ph.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div><Label className="text-xs text-[#5f7ba6]">Account Balance ($)</Label><Input data-testid="prop-balance" type="text" inputMode="decimal" value={prop.balance} onChange={(e) => setProp({ ...prop, balance: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="100000" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-12" /></div>

              <div className="border border-[#14233d] bg-[#060c18] rounded-2xl p-4 space-y-4" data-testid="prop-objectives">
                <div className="flex items-center gap-1.5 text-[#8fb4e6] font-semibold text-xs"><ShieldAlert className="h-3.5 w-3.5" /> Trading Objectives (your prop firm's rules)</div>
                <p className="text-[11px] text-[#5f7ba6] -mt-2">Enter the exact limits from your prop firm. Toggle $ or %. The Smart Scalper enforces these before every trade.</p>
                <ObjectiveRow label="Max Daily Loss" hint="e.g. 5 (%) or 300 ($)" obj={prop.objectives.daily_loss} onChange={(v) => setObj("daily_loss", v)} testid="obj-daily-loss" />
                <ObjectiveRow label="Max Loss / Maximum Drawdown" hint="e.g. 10 (%) or 1000 ($)" obj={prop.objectives.max_drawdown} onChange={(v) => setObj("max_drawdown", v)} testid="obj-max-drawdown" />
                <ObjectiveRow label="EOD Trailing Drawdown (if applicable)" hint="optional" obj={prop.objectives.trailing_drawdown} onChange={(v) => setObj("trailing_drawdown", v)} testid="obj-trailing" />
                <ObjectiveRow label="Profit Target" hint="e.g. 8 (%) or 1000 ($)" obj={prop.objectives.profit_target} onChange={(v) => setObj("profit_target", v)} testid="obj-profit-target" />
                <p className="text-[11px] text-[#5f7ba6] pt-1 border-t border-[#14233d]">Leave blank to use the {phaseCfg?.label || "phase"} defaults · Max Lot {phaseCfg?.max_lot} · Max Open {phaseCfg?.max_open_trades} · Risk/Trade {phaseCfg?.max_risk_per_trade_pct}%</p>
              </div>

              <div><Label className="text-xs text-[#5f7ba6]">MT5 Login</Label><Input data-testid="prop-account-id" inputMode="numeric" value={prop.account_id} onChange={(e) => setProp({ ...prop, account_id: e.target.value })} placeholder="510023987" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-12" /></div>
              <div><Label className="text-xs text-[#5f7ba6]">Password (master)</Label><Input data-testid="prop-password" type="password" value={prop.password} onChange={(e) => setProp({ ...prop, password: e.target.value })} placeholder="••••••••" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-12" /></div>
              <div><Label className="text-xs text-[#5f7ba6]">Broker Server</Label><Input data-testid="prop-server" value={prop.server} onChange={(e) => setProp({ ...prop, server: e.target.value })} placeholder="FTMO-Server3" className="mt-1 bg-[#060c18] border-[#14233d] rounded-xl h-12" /></div>
            </>
          )}
        </TabsContent>
      </Tabs>

      <div className="fixed bottom-0 left-0 right-0 max-w-[480px] mx-auto px-5 py-4 bg-[#060c18]/95 backdrop-blur border-t border-[#14233d]">
        <Button onClick={save} data-testid="save-account-button" disabled={busy || (tab === "propfirm" && propfirmLocked)}
          className="h-12 rounded-full text-white font-semibold w-full hover:opacity-90" style={{ backgroundColor: "var(--sfx-accent)" }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect Account"}
        </Button>
      </div>
    </div>
  );
}
